import { useEffect, useState } from "react";
import { Building2, CheckCircle2, ImageUp, Loader2, Mail, Send, Trash2 } from "lucide-react";
import { ApiError, fetchBlobUrl } from "@/lib/api";
import { Button, Field, Input, Select } from "@/components/ui/primitives";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import type { CompanySettings, EmailSettings } from "./api";
import { COMPANY_LOGO_PATH } from "./api";
import {
  useCompanySettings,
  useDeleteCompanyLogo,
  useEmailSettings,
  useSendTestEmail,
  useUpdateCompanySettings,
  useUpdateEmailSettings,
  useUploadCompanyLogo,
} from "./hooks";

export default function SettingsPage() {
  const { data, isLoading, isError, error } = useEmailSettings();
  const company = useCompanySettings();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Company profile and email delivery. Stored securely in the database —
          no config files to edit.
        </p>
      </div>

      {company.data && <CompanyProfileForm initial={company.data} />}

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {isError && (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : "Could not load settings."}
        </p>
      )}
      {data && <EmailSettingsForm initial={data} />}
    </div>
  );
}

function CompanyProfileForm({ initial }: { initial: CompanySettings }) {
  const [form, setForm] = useState({
    name: initial.name,
    address: initial.address,
    phone: initial.phone,
    email: initial.email,
    website: initial.website,
    gstin: initial.gstin,
  });
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateCompanySettings();
  const uploadLogo = useUploadCompanyLogo();
  const removeLogo = useDeleteCompanyLogo();
  const hasLogo = initial.has_logo;

  // Fetch the logo (auth'd) as an object URL for the preview; revoke on change.
  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    if (hasLogo) {
      // Cache-bust so a freshly uploaded logo replaces the old preview.
      fetchBlobUrl(`${COMPANY_LOGO_PATH}?t=${Date.now()}`)
        .then((u) => {
          if (cancelled) URL.revokeObjectURL(u);
          else {
            url = u;
            setLogoUrl(u);
          }
        })
        .catch(() => setLogoUrl(null));
    } else {
      setLogoUrl(null);
    }
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [hasLogo, initial.logo_mime]);

  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

  async function save() {
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync(form);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
    }
  }

  async function pickLogo(file: File) {
    setError(null);
    try {
      await uploadLogo.mutateAsync(file);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not upload the logo.");
    }
  }

  return (
    <section className="max-w-2xl space-y-5 rounded-lg border border-border bg-card p-6 text-card-foreground">
      <div className="flex items-center gap-2">
        <Building2 className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        <h2 className="font-semibold">Company profile</h2>
      </div>
      <p className="-mt-2 text-sm text-muted-foreground">
        Shown as the letterhead on report PDFs and the per-call service report.
      </p>

      {error && (
        <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Logo */}
      <div className="flex items-center gap-4">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40">
          {logoUrl ? (
            <img src={logoUrl} alt="Company logo" className="max-h-full max-w-full object-contain" />
          ) : (
            <Building2 className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          )}
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent">
              <ImageUp className="h-4 w-4" aria-hidden="true" />
              {hasLogo ? "Replace logo" : "Upload logo"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void pickLogo(file);
                  e.target.value = "";
                }}
              />
            </label>
            {hasLogo && (
              <ConfirmButton
                buttonVariant="outline"
                aria-label="Remove logo"
                title="Remove company logo?"
                message="Remove the company logo? PDFs will show the company name only."
                onConfirm={async () => {
                  await removeLogo.mutateAsync();
                }}
              >
                <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
              </ConfirmButton>
            )}
          </div>
          <p className="text-xs text-muted-foreground">PNG, JPEG, GIF or WebP · up to 2 MB.</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Company name" htmlFor="co_name">
          <Input id="co_name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Acme TechSoft Pvt Ltd" />
        </Field>
        <Field label="GSTIN" htmlFor="co_gstin">
          <Input id="co_gstin" value={form.gstin} onChange={(e) => set({ gstin: e.target.value })} placeholder="27ABCDE1234F1Z5" />
        </Field>
        <Field label="Phone" htmlFor="co_phone">
          <Input id="co_phone" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
        </Field>
        <Field label="Email" htmlFor="co_email">
          <Input id="co_email" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
        </Field>
        <Field label="Website" htmlFor="co_web">
          <Input id="co_web" value={form.website} onChange={(e) => set({ website: e.target.value })} placeholder="acme.example.com" />
        </Field>
        <Field label="Address" htmlFor="co_addr">
          <Input id="co_addr" value={form.address} onChange={(e) => set({ address: e.target.value })} placeholder="1 MG Road, Pune 411001" />
        </Field>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={() => void save()} disabled={update.isPending}>
          {update.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save profile
        </Button>
        {saved && (
          <span className="flex items-center gap-1 text-sm text-sla-ok">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            Saved
          </span>
        )}
      </div>
    </section>
  );
}

function EmailSettingsForm({ initial }: { initial: EmailSettings }) {
  const [form, setForm] = useState({
    provider: initial.provider,
    from_address: initial.from_address,
    from_name: initial.from_name,
    smtp_host: initial.smtp_host,
    smtp_port: initial.smtp_port,
    smtp_username: initial.smtp_username,
    smtp_password: "", // blank = keep the stored one
    smtp_use_tls: initial.smtp_use_tls,
  });
  const [passwordStored, setPasswordStored] = useState(initial.smtp_password_set);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState<string | null>(null);

  const update = useUpdateEmailSettings();
  const test = useSendTestEmail();
  const isSmtp = form.provider === "smtp";

  const set =
    <K extends keyof typeof form>(k: K) =>
    (v: (typeof form)[K]) => {
      setSaved(false);
      setForm((f) => ({ ...f, [k]: v }));
    };

  useEffect(() => {
    if (saved) {
      const t = setTimeout(() => setSaved(false), 3000);
      return () => clearTimeout(t);
    }
  }, [saved]);

  async function save() {
    setError(null);
    setSaved(false);
    try {
      const body = {
        provider: form.provider,
        from_address: form.from_address,
        from_name: form.from_name,
        smtp_host: form.smtp_host,
        smtp_port: form.smtp_port,
        smtp_username: form.smtp_username,
        smtp_use_tls: form.smtp_use_tls,
        // Only send the password when the admin typed one.
        ...(form.smtp_password ? { smtp_password: form.smtp_password } : {}),
      };
      const res = await update.mutateAsync(body);
      setPasswordStored(res.smtp_password_set);
      setForm((f) => ({ ...f, smtp_password: "" }));
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save settings.");
    }
  }

  async function sendTest() {
    setTestResult(null);
    setError(null);
    try {
      const res = await test.mutateAsync(testTo.trim());
      setTestResult(res.message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Test email failed.");
    }
  }

  return (
    <section className="max-w-2xl space-y-5 rounded-lg border border-border bg-card p-6 text-card-foreground">
      <div className="flex items-center gap-2">
        <Mail className="h-5 w-5 text-primary" aria-hidden="true" />
        <h2 className="font-semibold">Email delivery</h2>
      </div>

      {error && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <Field label="Delivery mode" htmlFor="provider">
        <Select
          id="provider"
          value={form.provider}
          onChange={(e) => set("provider")(e.target.value as "mock" | "smtp")}
        >
          <option value="mock">Mock — queue only, don&apos;t actually send (default)</option>
          <option value="smtp">SMTP — send real email</option>
        </Select>
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="From address" htmlFor="from_address">
          <Input
            id="from_address"
            type="email"
            value={form.from_address}
            onChange={(e) => set("from_address")(e.target.value)}
            placeholder="helpdesk@yourcompany.com"
          />
        </Field>
        <Field label="From name" htmlFor="from_name">
          <Input
            id="from_name"
            value={form.from_name}
            onChange={(e) => set("from_name")(e.target.value)}
            placeholder="3Handshake HelpDesk"
          />
        </Field>
      </div>

      <fieldset
        className="space-y-4 rounded-md border border-border p-4 disabled:opacity-60"
        disabled={!isSmtp}
      >
        <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          SMTP server {isSmtp ? "" : "(enable “SMTP” above to edit)"}
        </legend>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Field label="Host" htmlFor="smtp_host">
              <Input
                id="smtp_host"
                value={form.smtp_host}
                onChange={(e) => set("smtp_host")(e.target.value)}
                placeholder="smtp.gmail.com"
              />
            </Field>
          </div>
          <Field label="Port" htmlFor="smtp_port">
            <Input
              id="smtp_port"
              type="number"
              value={form.smtp_port}
              onChange={(e) => set("smtp_port")(Number(e.target.value))}
            />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Username" htmlFor="smtp_username">
            <Input
              id="smtp_username"
              autoComplete="off"
              value={form.smtp_username}
              onChange={(e) => set("smtp_username")(e.target.value)}
            />
          </Field>
          <Field
            label="Password"
            htmlFor="smtp_password"
            error={undefined}
          >
            <Input
              id="smtp_password"
              type="password"
              autoComplete="new-password"
              value={form.smtp_password}
              onChange={(e) => set("smtp_password")(e.target.value)}
              placeholder={passwordStored ? "•••••••• (unchanged)" : "Enter a password"}
            />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.smtp_use_tls}
            disabled={!isSmtp}
            onChange={(e) => set("smtp_use_tls")(e.target.checked)}
          />
          Use STARTTLS (recommended)
        </label>
      </fieldset>

      <div className="flex items-center gap-3">
        <Button onClick={() => void save()} disabled={update.isPending}>
          {update.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save settings
        </Button>
        {saved && (
          <span className="inline-flex items-center gap-1.5 text-sm text-sla-ok">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            Saved
          </span>
        )}
      </div>

      <div className="border-t border-border pt-4">
        <p className="mb-2 text-sm font-medium">Send a test email</p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[220px] flex-1">
            <Input
              aria-label="Test recipient"
              type="email"
              value={testTo}
              onChange={(e) => {
                setTestResult(null);
                setTestTo(e.target.value);
              }}
              placeholder="you@yourcompany.com"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => void sendTest()}
            disabled={test.isPending || testTo.trim().length < 3}
          >
            {test.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="h-4 w-4" aria-hidden="true" />
            )}
            Send test
          </Button>
        </div>
        {testResult && <p className="mt-2 text-sm text-sla-ok">{testResult}</p>}
        <p className="mt-2 text-xs text-muted-foreground">
          In “Mock” mode this always succeeds without sending. Save your SMTP settings
          before testing a real send.
        </p>
      </div>
    </section>
  );
}
